import { get } from './apiClient';

/** One document in a stage of an RFQ's lifecycle (GET /rfqs/lifecycle). Statuses are the stored words. */
export interface LifecycleDocDto {
  id: string;
  /** The document's human number; '' when it has none (a vendor invoice the reader could not number). */
  number: string;
  status: string;
  /** Vendor PO and vendor invoice only. */
  vendorName?: string;
}

export type LifecycleStageKey =
  | 'quotation'
  | 'order'
  | 'purchaseOrders'
  | 'vendorInvoices'
  | 'clientInvoices'
  | 'creditNotes'
  | 'payments';

/**
 * One RFQ's documents. A stage the caller may not see is ABSENT from the object (not loaded);
 * a stage they may see with nothing in it is `[]` (none exist).
 */
export type LifecycleDto = Partial<Record<LifecycleStageKey, LifecycleDocDto[]>>;

export interface LifecycleResponse {
  success: boolean;
  data: {
    /** rfq _id -> its documents. An RFQ the server could not find is not in the map. */
    lifecycles: Record<string, LifecycleDto>;
    stages: LifecycleStageKey[];
  };
}

/** The server refuses more than this many RFQs per call (rfqLifecycle.service MAX_RFQS). */
export const LIFECYCLE_MAX_IDS = 200;

/**
 * The documents behind these RFQs. More than the server's cap are sent as several calls, never cut short:
 * an RFQ left out would read as having no documents.
 */
export async function fetchRfqLifecycles(
  rfqIds: string[],
  init?: { signal?: AbortSignal }
): Promise<Record<string, LifecycleDto>> {
  const chunks: string[][] = [];
  for (let i = 0; i < rfqIds.length; i += LIFECYCLE_MAX_IDS) chunks.push(rfqIds.slice(i, i + LIFECYCLE_MAX_IDS));
  const answers = await Promise.all(
    chunks.map((ids) => get<LifecycleResponse>(`/rfqs/lifecycle?${new URLSearchParams({ ids: ids.join(',') })}`, init))
  );
  return Object.assign({}, ...answers.map((a) => a.data.lifecycles));
}

/** The RFQ behind one open document, with its names and lifecycle (GET /rfqs/lifecycle/document). */
export interface DocumentTrailDto {
  rfq: { _id: string; rfqId: string; status: string; customerName: string; vesselName: string };
  lifecycle: LifecycleDto;
}

/** The document kinds the server accepts (rfqLifecycle.service DOCUMENT_TYPES). */
export type LifecycleDocumentType =
  | 'rfq'
  | 'quotation'
  | 'order'
  | 'purchaseOrder'
  | 'vendorInvoice'
  | 'clientInvoice'
  | 'creditNote';

/** null is the server's positive "this document is in no RFQ's chain" (a manual quotation or invoice). */
export async function fetchTrailForDocument(
  type: LifecycleDocumentType,
  id: string,
  init?: { signal?: AbortSignal }
): Promise<DocumentTrailDto | null> {
  const res = await get<{ success: boolean; data: { trail: DocumentTrailDto | null } }>(
    `/rfqs/lifecycle/document?${new URLSearchParams({ type, id })}`,
    init
  );
  return res.data.trail;
}
